import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CrmService } from '../crm.service';
import { CrmLead, CrmDuplicatePair, CrmRoleOption, crmRoleLabel } from '../crm.model';
import { Router, RouterModule } from '@angular/router';
import { ToolbarService } from '../../services/toolbar.service';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { MessageService, ConfirmationService, MenuItem } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';
import { FormsModule } from '@angular/forms';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { RippleModule } from 'primeng/ripple';
import { TooltipModule } from 'primeng/tooltip';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { finalize } from 'rxjs';
import { PageResponse } from '../crm.model';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageSearchPanelComponent } from '../../shared/page-layout/page-search-panel.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';

@Component({
  selector: 'app-leads-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    DialogModule,
    ToastModule,
    BreadcrumbModule,
    ConfirmDialogModule,
    TagModule,
    IconFieldModule,
    InputIconModule,
    RippleModule,
    TooltipModule,
    MultiSelectModule,
    SelectModule,
    TextareaModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageSearchPanelComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './leads-list.component.html',
  styleUrls: ['./leads-list.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LeadsListComponent implements OnInit {
  private crmService = inject(CrmService);
  private toolbarService = inject(ToolbarService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private router = inject(Router);

  public leads = signal<CrmLead[]>([]);
  public loading = signal<boolean>(false);
  public totalRecords = signal<number>(0);
  public first = signal<number>(0);
  public rows = signal<number>(10);
  public searchText = signal<string>('');
  public statusFilter = signal<string>('');
  public sourceFilter = signal<string>('');
  public roleFilters = signal<string[]>([]);
  public columnFilters = signal<Record<string, string>>({});
  public crmRoles = signal<CrmRoleOption[]>([]);
  public duplicateDialog = signal<boolean>(false);
  public duplicates = signal<CrmDuplicatePair<CrmLead>[]>([]);
  public lastUpdated = signal<Date | null>(null);
  public scorecards = signal<Record<string, any>>({});
  public homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
  public breadcrumbItems: MenuItem[] = [
    { label: 'CRM', routerLink: '/crm/leads' },
    { label: 'Leads', routerLink: '/crm/leads' }
  ];
  public metrics = computed<PageMetric[]>(() => [
    { label: 'Total leads', value: Number(this.scorecards()['total'] || this.totalRecords()) },
    { label: 'New leads', value: Number(this.scorecards()['new'] || 0), tone: 'info' },
    { label: 'B2B leads', value: Number(this.scorecards()['b2b'] || 0) },
    { label: 'Converted', value: Number(this.scorecards()['converted'] || 0), tone: 'success' }
  ]);

  public statuses = [
    { label: 'New', value: 'NEW' },
    { label: 'Qualified', value: 'QUALIFIED' },
    { label: 'Negotiation', value: 'NEGOTIATION' },
    { label: 'Lost', value: 'LOST' }
  ];

  ngOnInit() {
    this.loadRoles();
    this.loadScorecards();
    this.loadLeads();
  }

  loadRoles() {
    this.crmService.getCrmRoles().subscribe({
      next: roles => this.crmRoles.set(roles || []),
      error: () => this.crmRoles.set([])
    });
  }

  loadScorecards() {
    this.crmService.getLeadScorecards(this.toolbarService.customer()?.company?.id).subscribe(cards => this.scorecards.set(cards || {}));
  }

  loadLeads(event?: TableLazyLoadEvent) {
    this.loading.set(true);
    if (event) {
      this.first.set(event.first ?? 0);
      this.rows.set(event.rows ?? 10);
    }
    const page = Math.floor(this.first() / this.rows());
    this.crmService.searchLeads({
      companyId: this.toolbarService.customer()?.company?.id,
      searchText: this.searchText(),
      status: this.statusFilter(),
      source: this.sourceFilter(),
      leadNumber: this.columnFilters()['leadNumber'] || '',
      companyName: this.columnFilters()['companyName'] || '',
      pocName: this.columnFilters()['pocName'] || '',
      pocEmail: this.columnFilters()['pocEmail'] || '',
      pocPhoneNumber: this.columnFilters()['pocPhoneNumber'] || '',
      leadQuality: this.columnFilters()['leadQuality'] || '',
      roles: this.roleFilters(),
      page,
      size: this.rows(),
      sortBy: 'createdAt',
      sortDir: 'desc'
    }).pipe(finalize(() => this.loading.set(false))).subscribe({
      next: (response: PageResponse<CrmLead>) => {
        this.leads.set(response.content || []);
        this.totalRecords.set(response.totalElements || 0);
        this.lastUpdated.set(new Date());
        this.loadScorecards();
      },
      error: () => this.loading.set(false)
    });
  }

  applySearch() {
    this.first.set(0);
    this.loadLeads();
  }

  clearSearch() {
    this.searchText.set('');
    this.statusFilter.set('');
    this.sourceFilter.set('');
    this.roleFilters.set([]);
    this.columnFilters.set({});
    this.first.set(0);
    this.loadLeads();
  }

  setSearchText(value: string) {
    this.searchText.set(value || '');
  }

  setStatusFilter(value: string) {
    this.statusFilter.set(value || '');
  }

  setSourceFilter(value: string) {
    this.sourceFilter.set(value || '');
  }

  setRoleFilters(values: string[] | null | undefined) {
    this.roleFilters.set(values || []);
  }

  setColumnFilter(field: string, value: string) {
    this.columnFilters.update(filters => ({ ...filters, [field]: value || '' }));
  }

  onSearchChange(value: string) {
    this.searchText.set(value);
    this.first.set(0);
    this.loadLeads();
  }

  onStatusFilterChange(value: string) {
    this.statusFilter.set(value);
    this.first.set(0);
    this.loadLeads();
  }

  onSourceFilterChange(value: string) {
    this.sourceFilter.set(value);
    this.first.set(0);
    this.loadLeads();
  }

  onColumnFilterChange(field: string, value: string) {
    this.columnFilters.update(filters => ({ ...filters, [field]: value || '' }));
    this.first.set(0);
    this.loadLeads();
  }

  loadDuplicates() {
    this.crmService.getLeadDuplicates(this.toolbarService.customer()?.company?.id).subscribe({
      next: (response) => {
        this.duplicates.set(response.data?.pairs || []);
        this.duplicateDialog.set(true);
      }
    });
  }

  mergeDuplicate(pair: CrmDuplicatePair<CrmLead>) {
    if (!pair.primary.id || !pair.duplicate.id) return;
    this.confirmationService.confirm({
      header: 'Merge duplicate lead',
      message: `Merge <b>${pair.duplicate.companyName}</b> into <b>${pair.primary.companyName}</b>?`,
      accept: () => {
        this.crmService.mergeLead(pair.primary.id!, pair.duplicate.id!, this.toolbarService.customer()?.company?.id).subscribe({
          next: (response) => {
            if (response.success) {
              this.messageService.add({ severity: 'success', summary: 'Merged', detail: 'Lead duplicates merged successfully' });
              this.duplicateDialog.set(false);
              this.loadLeads();
            }
          }
        });
      }
    });
  }

  openNew() {
    this.router.navigate(['/crm/leads/new']);
  }

  editLead(lead: CrmLead) {
    if (lead.id) {
      this.router.navigate(['/crm/leads', lead.id, 'edit']);
    }
  }

  deleteLead(lead: CrmLead) {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete lead from: <b>${lead.companyName}</b>?`,
      header: 'Confirm Deletion',
      icon: 'pi pi-exclamation-circle',
      acceptButtonStyleClass: 'p-button-danger p-button-text',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      accept: () => {
        if (lead.id) {
          this.crmService.deleteLead(lead.id).subscribe(() => {
            this.leads.set(this.leads().filter((val) => val.id !== lead.id));
            this.messageService.add({ severity: 'success', summary: 'Successful', detail: 'Lead Deleted', life: 3000 });
          });
        }
      }
    });
  }

  getSeverity(status: string) {
    switch (status) {
      case 'QUALIFIED':
        return 'success';
      case 'NEW':
        return 'info';
      case 'NEGOTIATION':
        return 'warn';
      case 'LOST':
        return 'danger';
      default:
        return 'secondary';
    }
  }

  roleLabel(role?: string | null): string {
    return crmRoleLabel(role);
  }

  get activeSearchCount(): number {
    const filters = this.columnFilters();
    return [
      this.searchText(),
      this.statusFilter(),
      this.sourceFilter(),
      this.roleFilters().length ? this.roleFilters().join(',') : '',
      filters['leadNumber'],
      filters['companyName'],
      filters['pocName'],
      filters['pocEmail'],
      filters['pocPhoneNumber'],
      filters['leadQuality']
    ].filter(value => !!String(value || '').trim()).length;
  }

  get openLeadCount(): number {
    return this.leads().filter(lead => !lead.converted).length;
  }

  get convertedLeadCount(): number {
    return this.leads().filter(lead => lead.converted).length;
  }

  get b2bLeadCount(): number {
    return this.leads().filter(lead => lead.businessType === 'B2B').length;
  }

  get lastUpdatedText(): string {
    const updated = this.lastUpdated();
    return updated ? `Updated ${updated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : '';
  }
}
